/**
 * Pure transform functions: BE API responses → component props.
 *
 * This is the ONLY file that knows the BE response shape.
 * Components only see the shapes defined here.
 * All functions are pure (no side effects, no React imports).
 */

import { formatCompactOneDecimal } from "../DashboardV2Page/constants";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// A signal's chip shows its unified level (PAN-818 spec §8), the same one the priority list, the
// HubSpot app, the writeback and the digests show. Stored severity is the fallback for an alert
// that is not a signal (level null) or an API that predates the field. An opportunity (a positive
// signal or an expansion action, `is_opportunity` from the API) gets its own chip instead of a
// level; another signal with no level (renewal due) reads Low.
const LEVEL_TO_IMPACT = {
  critical: "critical",
  high: "high",
  medium: "medium",
  low: "low",
  none: "low",
};
const SEVERITY_TO_IMPACT = { critical: "high", warning: "medium", info: "low" };

const CATEGORY_TO_SOURCE_TYPE = {
  usage: "usage",
  feature: "usage",
  communication: "email",
  revenue: "crm",
  health: "usage",
  support: "support",
};

// Maps structured evidence source → display labels
const SOURCE_LABELS = {
  usage: "Product usage",
  revenue: "Revenue data",
  account: "Account data",
  communication: "Communications",
};

const HEALTH_THRESHOLDS = { healthy: 70, needsAttention: 40 };

// usage_health_score is 0-10 (AccountUsageSummary); HEALTH_THRESHOLDS are 0-100.
const USAGE_HEALTH_SCALE = 10;

// ---------------------------------------------------------------------------
// Helpers (private)
// ---------------------------------------------------------------------------

/**
 * Format a Money object from DRF MoneyField → compact display string.
 * Input shapes: { amount: "12000.00", currency: "EUR" } or null.
 */
const formatMoney = (money) => {
  if (!money || money.amount == null) return "—";
  const amount = parseFloat(money.amount);
  const currency = money.currency ?? "EUR";

  // Compact display (1.2M, 45.3K, 15K): the dashboard priority row's rule (PAN-910)
  const formatCompact = formatCompactOneDecimal;

  try {
    // Use Intl to get the correct currency symbol for any currency
    const parts = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
    }).formatToParts(0);
    const symbol =
      parts.find((p) => p.type === "currency")?.value ?? `${currency} `;
    return `${symbol}${formatCompact(amount)}`;
  } catch {
    return `${currency} ${formatCompact(amount)}`;
  }
};

/** "2025-04-01" → "Apr 2025" */
const formatMonthYear = (isoDate) => {
  if (!isoDate) return "—";
  const d = new Date(isoDate + "T00:00:00"); // prevent timezone shift
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

/** "2025-04-01" → "01 Apr 2025" */
const formatFullDate = (isoDate) => {
  if (!isoDate) return "—";
  const d = new Date(isoDate + "T00:00:00");
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * "Aug 06 2029" → "06 Aug 2029". The account serializer pre-formats renewal_date
 * as "%b %d %Y"; re-render it like formatFullDate so the card reads one way.
 * Anything else passes through unchanged rather than being hidden.
 */
const formatServerDate = (value) => {
  if (!value) return "—";
  const m = /^([A-Z][a-z]{2}) (\d{2}) (\d{4})$/.exec(value);
  const month = m ? MONTHS.indexOf(m[1]) + 1 : 0;
  if (!month) return value;
  return formatFullDate(`${m[3]}-${String(month).padStart(2, "0")}-${m[2]}`);
};

/** 340 → "11 months 10 days" */
const formatLifespan = (totalDays) => {
  if (totalDays == null) return "—";
  const months = Math.floor(totalDays / 30);
  const days = totalDays % 30;
  const parts = [];
  if (months > 0) parts.push(`${months} month${months !== 1 ? "s" : ""}`);
  if (days > 0 || parts.length === 0)
    parts.push(`${days} day${days !== 1 ? "s" : ""}`);
  return parts.join(" ");
};

/** ISO datetime → "2 hours ago", "3 days ago", etc. */
const formatTimeAgo = (isoDatetime) => {
  if (!isoDatetime) return "";
  const seconds = Math.floor((Date.now() - new Date(isoDatetime)) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days !== 1 ? "s" : ""} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months !== 1 ? "s" : ""} ago`;
};

/**
 * Format date as "Feb 4, 2026 (approx. 2 months ago)".
 * For recent items (<24h), returns just the relative time.
 */
const formatDateWithRelative = (isoDatetime) => {
  if (!isoDatetime) return "";
  const date = new Date(isoDatetime);
  const relative = formatTimeAgo(isoDatetime);
  const seconds = Math.floor((Date.now() - date) / 1000);
  // For very recent items, just show relative
  if (seconds < 86400) return relative;
  const formatted = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return `${formatted} (approx. ${relative})`;
};

/** Numeric health score → status label. */
const scoreToStatus = (score) => {
  if (score == null) return "Unknown";
  const numScore = typeof score === "string" ? parseFloat(score) : score;
  if (numScore >= HEALTH_THRESHOLDS.healthy) return "Healthy";
  if (numScore >= HEALTH_THRESHOLDS.needsAttention) return "Needs Attention";
  return "At Risk";
};

/** Numeric health score → FE status key for styling. */
const scoreToStatusKey = (score) => {
  if (score == null) return "needs_attention";
  const numScore = typeof score === "string" ? parseFloat(score) : score;
  if (numScore >= HEALTH_THRESHOLDS.healthy) return "healthy";
  if (numScore >= HEALTH_THRESHOLDS.needsAttention) return "needs_attention";
  return "at_risk";
};

/** Confidence number → label. */
const getConfidenceLabel = (confidence) => {
  if (confidence == null) return null;
  if (confidence >= 80) return "High";
  if (confidence >= 50) return "Med";
  return "Low";
};

/** Round a change percent for display (null → 0). */
const roundChange = (val) => (val != null ? Math.round(val * 10) / 10 : null);

/** True when an ISO datetime is set and still in the future. */
const isFutureSnooze = (snoozedUntil) => {
  if (!snoozedUntil) return false;
  const t = new Date(snoozedUntil).getTime();
  return !Number.isNaN(t) && t > Date.now();
};

/**
 * Derive the FE signal lifecycle status from BE status + snooze_until.
 *
 * Lifecycle: active → resolved | snoozed | rejected.
 *
 * Rules:
 * - A non-active status always defines the state (resolved, or dismissed →
 *   "rejected"); snooze_until is ignored in that case.
 * - Snooze is NOT a backend status: a signal is "snoozed" only while its
 *   status is active AND snooze_until is set and in the future. Once that
 *   passes (or is cleared), the signal is "active" again automatically.
 */
const deriveSignalStatus = (beStatus, snoozeUntil, feedbackState) => {
  // A reject leaves status "active" and sets feedback_state "dismissed"; it wins
  // over resolved so a dismissed + resolved row is in Rejected (PAN-947).
  if (feedbackState === "dismissed") return "rejected";
  if (beStatus === "resolved") return "resolved";
  if (beStatus === "dismissed" || beStatus === "rejected") return "rejected";
  // Active (or unset): a future snooze defers it to the Snoozed tab.
  return isFutureSnooze(snoozeUntil) ? "snoozed" : "active";
};

/** Map BE renewal_forecast → FE key. */
const mapRenewalForecast = (forecast) => {
  if (!forecast) return "unknown";
  const label = typeof forecast === "object" ? forecast.value : forecast;
  const mapping = {
    growth: "renewal_likely",
    flat: "renewal_likely",
    down: "at_risk",
    churn: "churn_expected",
  };
  return mapping[label.toLowerCase()] ?? "unknown";
};

/**
 * Short display labels for churn reason slugs.
 * Derived from CANONICAL_CHURN_REASONS in the BE (signal_reason_guidance.py).
 * Key drivers should be 2-4 word tags, not full sentences.
 */
const CHURN_REASON_LABELS = {
  budget_price: "Budget / Price",
  lack_of_resources: "Lack of resources",
  roi_value: "ROI / Value concerns",
  competitor: "Going to competitor",
  stakeholder_changes: "Stakeholder changes",
  product_dissatisfaction: "Product dissatisfaction",
  bad_fit: "Bad fit",
  poor_customer_service: "Poor customer service",
  failed_onboarding: "Failed onboarding",
  outgrew_product: "Outgrew product",
  ma_restructuring: "M&A / Restructuring",
  company_shutdown: "Company shutdown",
  other: "Other",
};

/**
 * Normalize key drivers from churn reasons + risk factors into string array.
 * BE shapes vary: churn reasons can be {slug, evidence, confidence} objects or strings,
 * risk_factors are always strings.
 */
const normalizeKeyDrivers = (churnReasons, riskFactors) => {
  const reasons = churnReasons ?? [];
  const risks = riskFactors ?? [];

  // Prefer churn reasons if present, fall back to risk factors
  const source = reasons.length ? reasons : risks;
  return source.map((item) => {
    if (typeof item === "string") return item;
    // Object shapes: {slug, evidence, confidence, agrees_with_rules}
    // Show short label from slug — not verbose LLM evidence text
    if (item?.slug && CHURN_REASON_LABELS[item.slug]) {
      return CHURN_REASON_LABELS[item.slug];
    }
    return item?.slug ?? item?.evidence ?? String(item);
  });
};

/** Format a source system + last synced into a citation string. */
const formatSourceCitation = (sourceSystem, lastSynced) => {
  if (!sourceSystem) return null;
  const parts = [`Source: ${sourceSystem}`];
  if (lastSynced) {
    parts.push(`Synced ${formatTimeAgo(lastSynced)}`);
  }
  return parts.join(" · ");
};

/** Display name for source evidence types. */
const SOURCE_TYPE_LABELS = {
  email: "Email",
  note: "CSM Note",
  meeting: "Meeting",
};

/** Sentiment label for display. */
const formatSentimentBadge = (sentiment) => {
  if (!sentiment) return null;
  return sentiment.charAt(0).toUpperCase() + sentiment.slice(1);
};

/**
 * Build evidence items from structured_evidence + source_evidence.
 * Returns { metricEvidence: [...], sourceEvidence: [...] }.
 *
 * metricEvidence: from structured_evidence (numeric signals with thresholds)
 * sourceEvidence: from source_evidence (actual emails, notes, meetings)
 */
const buildEvidence = (alert) => {
  const metricEvidence = [];
  const sourceEvidence = [];

  // --- Metric evidence (from structured_evidence) ---
  const structured = alert.structured_evidence ?? [];
  structured.forEach((ev, i) => {
    metricEvidence.push({
      id: `${alert.id}-metric-${i}`,
      kind: "metric",
      sourceType: ev.source ?? "usage",
      sourceName: SOURCE_LABELS[ev.source] ?? ev.source,
      timestamp: formatTimeAgo(alert.created_at),
      headline: ev.label,
      subline: ev.interpretation ?? ev.context ?? null,
      detail: null, // Metrics don't expand — interpretation is in subline
      severity: ev.severity,
      metric: ev.metric,
      value: ev.value,
      sourceSystem: ev.source_system ?? null,
      lastSynced: ev.last_synced ?? null,
      citation: formatSourceCitation(ev.source_system, ev.last_synced),
    });
  });

  // --- Source evidence (from source_evidence — emails, notes, meetings) ---
  const sources = alert.source_evidence ?? [];
  sources.forEach((ev, i) => {
    const typeLabel = SOURCE_TYPE_LABELS[ev.type] ?? ev.type;
    const headline =
      ev.type === "email"
        ? ev.subject || "Untitled email"
        : ev.type === "meeting"
          ? ev.subject || "Untitled meeting"
          : typeLabel;
    const sentimentBadge = formatSentimentBadge(ev.sentiment);
    const dateStr = ev.date ? formatDateWithRelative(ev.date) : "";
    const sublineParts = [typeLabel, dateStr, sentimentBadge].filter(Boolean);

    sourceEvidence.push({
      id: `${alert.id}-source-${i}`,
      kind: "source",
      sourceType: ev.type === "email" || ev.type === "note" ? "email" : "call",
      sourceName: typeLabel,
      timestamp: dateStr,
      headline,
      subline: sublineParts.join(" · "),
      detail: ev.snippet || null,
      detailContext: ev.snippet_context || null,
      direction: ev.direction ?? null,
      sentiment: ev.sentiment ?? null,
      sentimentScore: ev.sentiment_score ?? null,
      fromEmail: ev.from_email ?? null,
      toEmails: ev.to_emails ?? null,
      sourceSystem: ev.source_system ?? null,
      lastSynced: ev.last_synced ?? null,
      citation: formatSourceCitation(ev.source_system, ev.last_synced),
      sourceUrl: ev.source_url ?? null,
    });
  });

  // --- Fallback: no structured or source evidence at all ---
  if (metricEvidence.length === 0 && sourceEvidence.length === 0) {
    const sourceType = CATEGORY_TO_SOURCE_TYPE[alert.category] ?? "usage";
    const ai = alert.ai_analysis ?? {};

    const supportingEvidence = ai.supporting_evidence ?? [];
    supportingEvidence.forEach((ev, i) => {
      const text = typeof ev === "string" ? ev : (ev?.text ?? String(ev));
      metricEvidence.push({
        id: `${alert.id}-ev-se-${i}`,
        kind: "metric",
        sourceType,
        sourceName: "AI analysis",
        timestamp: formatTimeAgo(alert.created_at),
        headline: text,
        subline: null,
        detail: null,
        citation: null,
      });
    });
  }

  return { metricEvidence, sourceEvidence };
};

// ---------------------------------------------------------------------------
// Public transforms
// ---------------------------------------------------------------------------

/**
 * Account detail → CustomerHeader + ClientOverviewCard props.
 * @param {Object} account - Response from GET /accounts/{id}/
 */
export const transformAccount = (account) => {
  const isPilot = account.subscription_status === "pilot";
  return {
    logo: account.logo ?? null,
    name: account.name ?? "—",
    arr: formatMoney(isPilot ? account.pilot_arr : account.arr),
    mrr: formatMoney(isPilot ? account.pilot_mrr : account.mrr),
    clientSince: formatMonthYear(account.client_since),
    clientSinceFull: formatFullDate(account.client_since),
    // Pilots show pilot_end_date (ISO, from the team's mapped HubSpot property),
    // the same field the dashboard's "Pilot ends in Xd" reads. Non-pilots keep
    // the subscription renewal_date.
    renewalDate: isPilot
      ? account.pilot_end_date
        ? formatFullDate(account.pilot_end_date)
        : "N/A"
      : account.renewal_date
        ? formatServerDate(account.renewal_date)
        : "N/A",
    lifespan: formatLifespan(account.customer_lifespan),
    cltv: formatMoney(account.customer_lifetime_value),
    csOwner: account.cs_owner_name ?? null,
    stage: account.lifecycle_stage ?? "customer",
    status: scoreToStatusKey(account.health_score),
    subscriptionStatus: account.subscription_status ?? null,
    // Pilot funnel for the PilotStageBar in the header. The BE `pilot` block
    // (AccountSerializer.get_pilot) carries the team's ordered, configured stage
    // list plus the account's current HubSpot stage id; the bar derives its
    // position from `stages.findIndex(s => s.id === currentStageId)`. Non-pilot
    // accounts get `pilot_stage_id: null` and the header skips rendering.
    pilot: account.pilot
      ? {
          currentStageId: account.pilot.pilot_stage_id ?? null,
          stages: account.pilot.pilot_deal_stages ?? [],
        }
      : null,
    slackChannelName: account.slack_channel_name ?? null,
  };
};

/**
 * Account detail → RenewalForecastCard props.
 * @param {Object} account - Response from GET /accounts/{id}/
 */
export const transformForecast = (account) => {
  // delta_mrr is the forecasted total MRR (current + expansion/contraction).
  // Expansion MRR = delta_mrr - current mrr. Show €0 when no change.
  const currentAmount = parseFloat(account.mrr?.amount ?? 0);
  const forecastAmount = parseFloat(account.delta_mrr?.amount ?? 0);
  const expansionAmount = forecastAmount - currentAmount;
  const currency =
    account.mrr?.currency ?? account.delta_mrr?.currency ?? "EUR";

  return {
    renewalForecast: mapRenewalForecast(account.renewal_forecast),
    renewalMrr: formatMoney(account.mrr),
    expansionPotential: account.has_expansion_potential ?? false,
    expansionMrr: formatMoney(
      expansionAmount !== 0
        ? { amount: expansionAmount.toFixed(2), currency }
        : { amount: "0.00", currency },
    ),
    forecastNote: account.forecast_note ?? null,
  };
};

/**
 * AI insights response → summary string for AISummaryCard.
 * @param {Object|null} insights - Response from GET /ai/accounts/{id}/insights/
 */
export const transformAISummary = (insights) =>
  insights?.summary ?? insights?.reasoning_summary ?? null;

/**
 * Single AccountAlert → signal object for SignalRow.
 * @param {Object} alert - Item from actions list endpoint
 */
export const transformAlertToSignal = (alert) => {
  const status = deriveSignalStatus(
    alert.status,
    alert.snooze_until,
    alert.feedback_state,
  );

  // Attribution depends on which lifecycle action put the signal in its state.
  // The BE exposes per-action *_by_name / *_at fields (no generic ones).
  const lifecycleBy =
    status === "snoozed"
      ? (alert.snoozed_by_name ?? null)
      : status === "resolved"
        ? (alert.resolved_by_name ?? null)
        : status === "rejected"
          ? (alert.feedback_by_name ?? null)
          : null;
  const lifecycleAt =
    status === "snoozed"
      ? (alert.snoozed_at ?? null)
      : status === "resolved"
        ? (alert.resolved_at ?? null)
        : status === "rejected"
          ? (alert.feedback_at ?? null)
          : null;

  return {
    id: alert.id,
    alertType: alert.type ?? null,
    impact: alert.is_opportunity
      ? "opportunity"
      : (LEVEL_TO_IMPACT[alert.level] ??
        SEVERITY_TO_IMPACT[alert.severity] ??
        "low"),
    title: alert.title ?? alert.action_title ?? "Signal",
    createdAt: alert.created_at ?? null,
    timeAgo: formatTimeAgo(alert.created_at),
    // AI summary headline (#1163 follow-up): quote-anchored, more specific than
    // the templated alert.text. BE writes it only for ai_signal_summaries-enrolled
    // teams AND alerts with quotable evidence — everyone else falls back to text.
    description: alert.summary_headline || alert.text || "",
    recurrenceNote: alert.summary_recurrence_note ?? null,
    confidence: alert.ai_analysis?.confidence ?? null,
    confidenceLabel: getConfidenceLabel(alert.ai_analysis?.confidence),
    keyDrivers: normalizeKeyDrivers(
      alert.likely_churn_reasons,
      alert.risk_factors,
    ),
    actions: (alert.recommended_actions ?? []).map((item, i) => {
      const isObj = typeof item === "object" && item !== null;
      // Registry-backed signals return structured items {id, text, priority} and the
      // backend keys action_states by the item's stable id, so completion survives a
      // template reorder. Legacy/plain-string items have no id → key by index (the
      // pre-cutover behavior the backend still honors for them).
      const stateKey = isObj && item.id != null ? item.id : String(i);
      const savedState = (alert.action_states ?? {})[stateKey];
      return {
        id: `${alert.id}-act-${i}`,
        // Structured items use `text`; legacy objects used `action`.
        title: isObj ? (item.text ?? item.action ?? "Action") : String(item),
        priority: isObj ? (item.priority ?? null) : null,
        status: savedState?.status ?? "pending",
        note: savedState?.note ?? null,
      };
    }),
    evidence: buildEvidence(alert),
    status,
    // Lifecycle metadata — populated once a signal leaves the "active" state.
    snoozedUntil: alert.snooze_until ?? null,
    rejectReason: alert.dismiss_reason ?? null,
    resolvedAt: alert.resolved_at ?? null,
    // Plain system resolve (no person, no auto-resolve reason): PAN-948.
    resolvedAutomatically:
      status === "resolved" && alert.resolved_automatically === true,
    lifecycleBy,
    lifecycleAt,
    // Hypotheses-mode feedback fields (Stream B Step 3)
    feedbackState: alert.feedback_state ?? "pending",
    feedbackAt: alert.feedback_at ?? null,
    feedbackBy: alert.feedback_by ?? null,
    dismissReason: alert.dismiss_reason ?? null,
    dismissNote: alert.dismiss_note ?? null,
  };
};

/**
 * Usage summary → ProductUsageSection props.
 * @param {Object} summary - Mapped response from useUsageSummary
 * @param {Object} [account] - Account detail (provides confidence_level)
 */
export const transformUsage = (summary, account = null) => ({
  dau: { value: summary.dau ?? 0, change: roundChange(summary.dau_change) },
  wau: { value: summary.wau ?? 0, change: roundChange(summary.wau_change) },
  mau: { value: summary.mau ?? 0, change: roundChange(summary.mau_change) },
  health: {
    status: scoreToStatus(
      summary.health_score == null
        ? null
        : parseFloat(summary.health_score) * USAGE_HEALTH_SCALE,
    ),
    confidence: account?.confidence_level ?? null,
    lastActivity: summary.last_activity
      ? formatTimeAgo(summary.last_activity)
      : "N/A",
  },
});

// Export helpers for testing
export const _testHelpers = {
  formatMoney,
  formatMonthYear,
  formatFullDate,
  formatServerDate,
  formatLifespan,
  formatTimeAgo,
  formatDateWithRelative,
  scoreToStatus,
  scoreToStatusKey,
  getConfidenceLabel,
  mapRenewalForecast,
  buildEvidence,
  normalizeKeyDrivers,
};
